/* "Hangi tel?": the app plays an open string, you tap which one it was. Strings vibrate on a canvas. */
import { useEffect, useRef, useState } from 'react';
import { audio } from '../../core/audio';
import { addXp } from '../../core/store';
import { mod, openString, pcName } from '../../core/theory';
import { Btn } from '../../ui/Button';

const GA = [1.2, 1.6, 2.1, 2.8, 3.4, 4.0];
const nameOf = (sn: number) => `${sn}. tel, ${pcName(mod(openString(sn), 12))}`;

export function StringsGame() {
  const wrap = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [msg, setMsg] = useState('Açık tellerden birini çalacağım. Hangisi olduğunu bul.');
  const [score, setScore] = useState({ right: 0, total: 0 });
  const [ys, setYs] = useState<number[]>([]);
  const s = useRef({
    amp: [0, 0, 0, 0, 0, 0],
    born: [0, 0, 0, 0, 0, 0],
    raf: 0,
    W: 0,
    H: 0,
    target: null as number | null,
    last: null as number | null,
    reduced: false,
    draw: (_now: number) => {},
  }).current;

  useEffect(() => {
    const cv = canvas.current;
    const wr = wrap.current;
    const ctx = cv?.getContext('2d');
    if (!cv || !wr || !ctx) return;
    s.reduced = !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const yPos = () => {
      const pad = 22;
      return [0, 1, 2, 3, 4, 5].map((i) => pad + i * ((s.H - pad * 2) / 5));
    };
    s.draw = (now: number) => {
      ctx.clearRect(0, 0, s.W, s.H);
      const y = yPos();
      let active = false;
      for (let i = 0; i < 6; i++) {
        const age = (now - (s.born[i] as number)) / 1000;
        const a = (s.amp[i] as number) * Math.exp(-age * 2.4);
        if (a > 0.3) active = true;
        const wound = i >= 3;
        ctx.strokeStyle = wound ? '#c9a46a' : '#e7e3da';
        ctx.lineWidth = GA[i] as number;
        ctx.beginPath();
        const steps = 60;
        for (let k = 0; k <= steps; k++) {
          const x = (k / steps) * s.W;
          const env = Math.sin((Math.PI * k) / steps);
          const off = a * env * Math.sin((now / 1000) * (26 - i * 2.2) * Math.PI * 2);
          if (k === 0) ctx.moveTo(x, (y[i] as number) + off);
          else ctx.lineTo(x, (y[i] as number) + off);
        }
        ctx.stroke();
        if (wound) {
          ctx.strokeStyle = 'rgba(0,0,0,.25)';
          ctx.lineWidth = 0.6;
          ctx.beginPath();
          ctx.moveTo(0, (y[i] as number) + (GA[i] as number) / 3);
          ctx.lineTo(s.W, (y[i] as number) + (GA[i] as number) / 3);
          ctx.stroke();
        }
      }
      s.raf = active && !s.reduced ? requestAnimationFrame(s.draw) : 0;
    };
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      s.W = wr.clientWidth;
      s.H = wr.clientHeight;
      cv.width = Math.round(s.W * dpr);
      cv.height = Math.round(s.H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      setYs(yPos());
      s.draw(performance.now());
    };
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    const raf = requestAnimationFrame(() => {
      resize();
      ro?.observe(wr);
    });
    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(s.raf);
      ro?.disconnect();
    };
  }, [s]);

  const pluck = (i: number, strength = 9) => {
    s.amp[i] = s.reduced ? 0 : strength;
    s.born[i] = performance.now();
    if (!s.raf) s.raf = requestAnimationFrame(s.draw);
  };
  const playString = async (sn: number) => {
    await audio.resume();
    audio.playNote(openString(sn), { inst: 'guitar', dur: 2.2 });
  };
  const pick = (sn: number) => {
    const i = sn - 1;
    const target = s.target;
    if (target == null) {
      playString(sn);
      pluck(i);
      setMsg(`${nameOf(sn)}. Bir soru için "Bir tel çal" düğmesine bas.`);
      return;
    }
    if (sn === target) {
      setScore((x) => ({ right: x.right + 1, total: x.total + 1 }));
      setMsg(`Doğru! ${nameOf(sn)}.`);
      pluck(i, 11);
      playString(sn);
      addXp(2, 'nota');
    } else {
      setScore((x) => ({ ...x, total: x.total + 1 }));
      setMsg(`Bu ${nameOf(sn)}. Doğrusu ${nameOf(target)}.`);
      pluck(target - 1, 11);
      playString(target);
    }
    s.target = null;
  };
  const ask = async () => {
    let sn: number;
    let tries = 0;
    do sn = 1 + Math.floor(Math.random() * 6);
    while (sn === s.last && tries++ < 20);
    s.last = sn;
    s.target = sn;
    await playString(sn);
    for (let i = 0; i < 6; i++) pluck(i, 1.4);
    setMsg('Hangi teldi? Telin üzerine dokun.');
  };

  return (
    <div className="strings-card">
      <div className="sc-head">
        <span className="sc-title">Hangi tel?</span>
        <span className="sc-score">{score.total ? `${score.right} / ${score.total}` : ''}</span>
      </div>
      <div className="sc-canvas-wrap" ref={wrap}>
        {/* biome-ignore lint/a11y/noAriaHiddenOnFocusable: decorative canvas, never focusable */}
        <canvas className="sc-canvas" aria-hidden="true" ref={canvas} />
        <div style={{ position: 'absolute', inset: '0' }}>
          {[1, 2, 3, 4, 5, 6].map((sn) => (
            <button
              key={sn}
              type="button"
              className="sc-hit"
              aria-label={nameOf(sn)}
              style={ys[sn - 1] != null ? { top: `${ys[sn - 1]}px` } : undefined}
              onClick={() => pick(sn)}
            >
              <span className="sc-name">{sn}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="sc-foot">
        <div className="sc-msg">{msg}</div>
        <Btn icon="play" className="sc-btn" onClick={ask}>
          Bir tel çal
        </Btn>
      </div>
    </div>
  );
}
